/**
 * Limits concurrent hex image fetches. When viewport bounds change we only reject queued
 * waiters (so new visible tiles get slots as in-flight complete); we do not abort in-flight
 * to avoid errors and keep 60fps.
 * Crawler bypasses queue so all visible hexes load in parallel (speedtest/SEO).
 */

import { isCrawler } from "../utils/settings";

export const MAX_PARALLEL_HEX_LOADS = 16;

let active = 0;
let rejectedTotal = 0;
const queue: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

function acquire(): Promise<void> {
  if (active < MAX_PARALLEL_HEX_LOADS) {
    active++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    queue.push({ resolve, reject });
  });
}

function release(): void {
  active--;
  if (queue.length > 0 && active < MAX_PARALLEL_HEX_LOADS) {
    const next = queue.shift()!;
    active++;
    next.resolve();
  }
}

/**
 * Call when visible hex bounds change. Rejects all queued waiters so new visible tiles
 * get slots as current in-flight finish. Does not abort in-flight (no errors, smooth pan).
 */
export function clearQueueAndAbort(): void {
  const err = new Error("viewport changed");
  rejectedTotal += queue.length;
  while (queue.length > 0) {
    const entry = queue.shift()!;
    entry.reject(err);
  }
}

export function getHexLoaderStats(): { queue: number; active: number; rejected: number } {
  return { queue: queue.length, active, rejected: rejectedTotal };
}

const FETCH_OPTIONS: RequestInit = {
  cache: "force-cache",
  keepalive: false,
  headers: { Accept: "image/webp,image/png,image/jpeg,image/*,*/*" },
};

export async function fetchHexImage(url: string): Promise<Response> {
  if (isCrawler()) {
    return fetch(url);
  }
  await acquire();
  try {
    return await fetch(url, FETCH_OPTIONS);
  } finally {
    release();
  }
}
